/**
 * ActivateBundleModal.tsx — F20 T20.4.
 *
 * Modal hurtowej aktywacji pakietu modułów: select pakietu (aktywne z katalogu)
 * + seats + okres, podgląd ceny efektywnej po rabacie, POST
 * /api/admin/tenants/:id/bundles/:code/activate.
 *
 * Wzorzec 1:1 z ActivateModuleModal (bezpośredni api.post, bez useMutation;
 * toast/invalidacje robi wywołujący tab przez onSuccess).
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

interface BundleListItem {
  id: string;
  code: string;
  name: string | null;
  moduleCodes: string[];
  discountPercent: number;
  isActive: boolean;
  gcrecord: string | null;
  bundlePricePlnMonthly: number | null;
  bundlePricePlnYearly: number | null;
}

interface BundlesResponse {
  bundles: BundleListItem[];
}

export interface BundleActivationSummary {
  bundleCode: string;
  bundleName: string;
  activated: string[];
  skipped: Array<{ code: string; reason: string }>;
  failed: Array<{ code: string; error: string }>;
}

export interface ActivateBundleModalProps {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onSuccess: (summary: BundleActivationSummary) => void;
}

type Period = 1 | 12;

function formatPln(grosze: number): string {
  return (grosze / 100).toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function ActivateBundleModal({
  open,
  tenantId,
  onClose,
  onSuccess,
}: ActivateBundleModalProps) {
  const { t } = useTranslation();
  const formId = useId();
  const resolvedFormId = `activate-bundle-form-${formId}`;

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [seats, setSeats] = useState<string>('1');
  const [period, setPeriod] = useState<Period>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [seatsError, setSeatsError] = useState<string | null>(null);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedCode(null);
      setSeats('1');
      setPeriod(1);
      setIsSubmitting(false);
      setApiError(null);
      setSeatsError(null);
    }
    prevOpenRef.current = open;
  }, [open]);

  const bundlesQuery = useQuery({
    queryKey: ['admin', 'bundles', 'active'],
    queryFn: async (): Promise<BundlesResponse> => {
      const res = await api.get<BundlesResponse>('/admin/bundles');
      return res.data;
    },
    enabled: open,
    staleTime: 60 * 1000,
  });

  const activeBundles = (bundlesQuery.data?.bundles ?? []).filter(
    (b) => b.isActive && b.gcrecord === null,
  );
  const selected = activeBundles.find((b) => b.code === selectedCode) ?? activeBundles[0] ?? null;

  function validate(): boolean {
    const n = Number.parseInt(seats, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10_000) {
      setSeatsError(t('admin.tenants.detail.modules.activateBundleModal.errorSeats'));
      return false;
    }
    setSeatsError(null);
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selected || !validate()) return;
    setIsSubmitting(true);
    setApiError(null);

    try {
      const res = await api.post<Omit<BundleActivationSummary, 'bundleName'>>(
        `/admin/tenants/${tenantId}/bundles/${selected.code}/activate`,
        {
          seats: Number.parseInt(seats, 10),
          periodMonths: period,
        },
      );
      onSuccess({ ...res.data, bundleName: selected.name ?? selected.code });
    } catch {
      setApiError(t('admin.tenants.detail.modules.activateBundleModal.errorGeneric'));
      setIsSubmitting(false);
    }
  }

  const effectivePrice =
    selected === null
      ? null
      : period === 1
        ? selected.bundlePricePlnMonthly
        : selected.bundlePricePlnYearly;

  const priceLine =
    selected === null
      ? null
      : effectivePrice === null
        ? t('admin.tenants.detail.modules.activateBundleModal.priceUnavailable')
        : t(
            period === 1
              ? 'admin.tenants.detail.modules.activateBundleModal.priceMonthly'
              : 'admin.tenants.detail.modules.activateBundleModal.priceYearly',
            { price: formatPln(effectivePrice), discount: selected.discountPercent },
          );

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('admin.tenants.detail.modules.activateBundleModal.cancel')}
      </Button>
      <Button
        type="submit"
        form={resolvedFormId}
        loading={isSubmitting}
        disabled={isSubmitting || !selected}
      >
        {isSubmitting
          ? t('admin.tenants.detail.modules.activateBundleModal.submitting')
          : t('admin.tenants.detail.modules.activateBundleModal.submit')}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.tenants.detail.modules.activateBundleModal.title')}
      description={t('admin.tenants.detail.modules.activateBundleModal.description')}
      size="sm"
      footer={footer}
      closeOnBackdrop={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <form id={resolvedFormId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {bundlesQuery.isError ? (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {t('admin.tenants.detail.modules.activateBundleModal.loadError')}
          </div>
        ) : activeBundles.length === 0 && !bundlesQuery.isLoading ? (
          <p className="text-sm text-text-muted">
            {t('admin.tenants.detail.modules.activateBundleModal.empty')}
          </p>
        ) : (
          <>
            <FormField label={t('admin.tenants.detail.modules.activateBundleModal.bundleLabel')}>
              <Dropdown
                aria-label={t('admin.tenants.detail.modules.activateBundleModal.bundleLabel')}
                value={selected?.code ?? ''}
                onChange={(v) => setSelectedCode(v)}
                disabled={bundlesQuery.isLoading || isSubmitting}
                options={activeBundles.map((b) => ({ value: b.code, label: b.name ?? b.code }))}
              />
            </FormField>

            {priceLine && <p className="text-xs text-text-secondary">{priceLine}</p>}

            <Input
              label={t('admin.tenants.detail.modules.activateBundleModal.seatsLabel')}
              type="number"
              min={1}
              max={10_000}
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              error={seatsError ?? undefined}
              hint={
                seatsError
                  ? undefined
                  : t('admin.tenants.detail.modules.activateBundleModal.seatsHint')
              }
              required
            />
            <FormField label={t('admin.tenants.detail.modules.activateBundleModal.periodLabel')}>
              <div
                role="radiogroup"
                aria-label={t('admin.tenants.detail.modules.activateBundleModal.periodLabel')}
                className="flex flex-col gap-2"
              >
                <label className="inline-flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="radio"
                    name={`bundle-period-${formId}`}
                    value="1"
                    checked={period === 1}
                    onChange={() => setPeriod(1)}
                    className="h-4 w-4 accent-accent-600"
                  />
                  {t('admin.tenants.detail.modules.activateBundleModal.period1m')}
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="radio"
                    name={`bundle-period-${formId}`}
                    value="12"
                    checked={period === 12}
                    onChange={() => setPeriod(12)}
                    className="h-4 w-4 accent-accent-600"
                  />
                  {t('admin.tenants.detail.modules.activateBundleModal.period12m')}
                </label>
              </div>
            </FormField>
          </>
        )}

        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {apiError}
          </div>
        )}
      </form>
    </Modal>
  );
}
